import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Image, X, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PortfolioPhoto {
  id: string;
  file_path: string;
  file_name: string;
  description: string | null;
  created_at: string;
}

interface TradesmanPortfolioDialogProps {
  tradesmanId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TradesmanPortfolioDialog = ({ tradesmanId, open, onOpenChange }: TradesmanPortfolioDialogProps) => {
  const [photos, setPhotos] = useState<PortfolioPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tradesmanInfo, setTradesmanInfo] = useState<{
    company_name: string;
    trade_specialties: string[];
  } | null>(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open && tradesmanId) {
      fetchPortfolioPhotos();
      fetchTradesmanInfo();
    }
  }, [open, tradesmanId]);

  const fetchTradesmanInfo = async () => {
    if (!tradesmanId) return;

    try {
      const { data } = await supabase.rpc("get_public_tradesman_profile", { 
        tradesman_id: tradesmanId 
      });
      
      const profile = Array.isArray(data) ? data[0] : data;
      if (profile) {
        setTradesmanInfo({
          company_name: profile.company_name || "Tradesman",
          trade_specialties: profile.trade_specialties || []
        });
      }
    } catch (error) {
      console.error("Error fetching tradesman info:", error);
    }
  };

  const fetchPortfolioPhotos = async () => {
    if (!tradesmanId) return;

    setIsLoading(true);
    try {
      const { data: photosData, error } = await supabase
        .from("portfolio_photos")
        .select("id, file_path, file_name, description, created_at")
        .eq("tradesman_id", tradesmanId)
        .eq("status", "approved")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setPhotos(photosData || []);
    } catch (error) {
      console.error("Error fetching portfolio photos:", error);
      setPhotos([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getImageUrl = (filePath: string) => {
    const { data } = supabase.storage
      .from("portfolio-photos")
      .getPublicUrl(filePath);
    return data.publicUrl;
  };

  const openLightbox = (index: number) => {
    setSelectedPhotoIndex(index);
  };

  const closeLightbox = () => {
    setSelectedPhotoIndex(null);
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (selectedPhotoIndex === null) return;
    
    if (direction === 'prev') {
      setSelectedPhotoIndex(selectedPhotoIndex > 0 ? selectedPhotoIndex - 1 : photos.length - 1);
    } else {
      setSelectedPhotoIndex(selectedPhotoIndex < photos.length - 1 ? selectedPhotoIndex + 1 : 0);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Portfolio Photos
            </DialogTitle>
          </DialogHeader>

          {tradesmanInfo && (
            <div className="bg-muted/50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-lg mb-2">{tradesmanInfo.company_name}</h3>
              {tradesmanInfo.trade_specialties.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tradesmanInfo.trade_specialties.map((specialty) => (
                    <Badge key={specialty} variant="outline">
                      {specialty}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-sm text-muted-foreground">Loading portfolio...</p>
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Image className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No portfolio photos available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {photos.map((photo, index) => (
                <div 
                  key={photo.id} 
                  className="group relative aspect-square overflow-hidden rounded-lg border cursor-pointer hover:shadow-lg transition-all duration-200"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={getImageUrl(photo.file_path)}
                    alt={photo.description || photo.file_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
                  <div className="absolute bottom-2 left-2 right-2 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {photo.description && (
                      <p className="text-xs truncate bg-black/50 rounded px-2 py-1">
                        {photo.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox for full-size image viewing */}
      {selectedPhotoIndex !== null && photos[selectedPhotoIndex] && (
        <Dialog open={true} onOpenChange={closeLightbox}>
          <DialogContent className="max-w-6xl max-h-[90vh] p-0 border-0">
            <div className="relative bg-black/95 rounded-lg overflow-hidden">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
                onClick={closeLightbox}
              >
                <X className="h-6 w-6" />
              </Button>

              {photos.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:bg-white/20"
                    onClick={() => navigateLightbox('prev')}
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:bg-white/20"
                    onClick={() => navigateLightbox('next')}
                  >
                    <ChevronRight className="h-8 w-8" />
                  </Button>
                </>
              )}

              <div className="flex items-center justify-center min-h-[60vh] max-h-[80vh] p-4">
                <img
                  src={getImageUrl(photos[selectedPhotoIndex].file_path)}
                  alt={photos[selectedPhotoIndex].description || photos[selectedPhotoIndex].file_name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>

              {photos[selectedPhotoIndex].description && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-4">
                  <p className="text-center">{photos[selectedPhotoIndex].description}</p>
                  <p className="text-center text-sm text-gray-300 mt-1">
                    Added {formatDate(photos[selectedPhotoIndex].created_at)}
                  </p>
                </div>
              )}

              {photos.length > 1 && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                  {selectedPhotoIndex + 1} of {photos.length}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};